import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardBody, Eyebrow } from '@/shared/components';
import { useApi, type SyncActivityEvent } from '@/shared/api';
import { useApartmentDetail } from './ApartmentDetailLayout';
import { propertyKeys } from '../queries';
import { SyncActivityDetailsDrawer } from '../audit-view/SyncActivityDetailsDrawer';
import { SyncActivityRow } from '../audit-view/SyncActivityRow';
import { SyncActivitySummaryPill } from '../audit-view/SyncActivitySummaryPill';
import { SystemMetaCard } from '../audit-view/SystemMetaCard';

export function AuditTab() {
  const { propertyId: id } = useApartmentDetail();
  const api = useApi();
  const [detailEvent, setDetailEvent] = useState<SyncActivityEvent | null>(null);
  const { data: property } = useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api.properties.get(id),
  });

  const { data: syncActivity } = useQuery({
    queryKey: ['channex', 'syncActivity', id],
    queryFn: () => api.channex.syncActivity(id),
    enabled: !!property?.channexPropertyId,
    staleTime: 30_000,
  });

  return (
    <>
      <header className="mb-6">
        <Eyebrow>Apartment</Eyebrow>
        <h1 className="text-ink mt-2 font-serif text-3xl tracking-tight">Protokoll</h1>
        <p className="text-ink-soft mt-1.5 text-sm">
          Kennungen des Apartments und der Austausch mit dem Kanalmanager. Das vollständige
          Änderungsprotokoll des Workspace liegt unter Einstellungen → Änderungsprotokoll.
        </p>
      </header>

      <SystemMetaCard propertyId={id} property={property} />

      <Card>
        <CardBody className="p-6">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <Eyebrow variant="muted">Austausch mit dem Kanalmanager</Eyebrow>
              <p className="text-ink-soft mt-1 max-w-prose text-xs">
                Die letzten 20 Vorgänge des Objekts — eingehende Meldungen der Buchungsportale und
                ausgehende Übertragungen von Preisen und Verfügbarkeit. Der Kanalmanager adressiert
                das Objekt, nicht das einzelne Apartment; die Liste gilt deshalb für alle Apartments
                dieses Objekts.
              </p>
            </div>
            {syncActivity && <SyncActivitySummaryPill summary={syncActivity.summary} />}
          </div>

          {!property?.channexPropertyId ? (
            <p className="text-ink-soft text-sm">
              Das Objekt ist noch nicht beim Kanalmanager angelegt — es gibt nichts zu übertragen.
            </p>
          ) : !syncActivity ? (
            <p className="text-ink-soft text-sm" role="status">
              Vorgänge werden geladen…
            </p>
          ) : syncActivity.recent.length === 0 ? (
            <p className="text-ink-soft text-sm">
              Noch kein Vorgang. Sobald eine Buchung eintrifft oder Preise übertragen werden,
              erscheint sie hier.
            </p>
          ) : (
            <ul className="divide-line/40 divide-y">
              {syncActivity.recent.map((event, i) => (
                <SyncActivityRow
                  key={`${event.source}-${i}-${event.occurredAt}`}
                  event={event}
                  onOpenDetails={setDetailEvent}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <SyncActivityDetailsDrawer event={detailEvent} onClose={() => setDetailEvent(null)} />
    </>
  );
}
